import { useState, useEffect } from "react";
import DataTable, { Column } from "../../components/DataTable";
import PaginationBar from "../../components/PaginationBar";
import { Student } from "../../types/studentType";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store";
import {
  getStudents,
  setPagination,
  setSearchFilters,
} from "../../features/student/StudentSlice";

import useUrlTab from "../../util/hooks/useUrlTab";
import Tag from "../../components/Tags";
import DownloadButton from "../../components/common/DownloadButton";
import { exportToExcel } from "../../util/functions/UniversalFunctions";

const tabOptions = ["All Students", "Students with Daycare"];

export default function StudentsList() {
  const { activeTab, handleTabChange } = useUrlTab(tabOptions);
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const { students, isLoading, pagination, searchFilters } = useSelector(
    (state: RootState) => state.student,
  );

  const { schoolBranch } = useSelector((state: RootState) => state.access);

  const branchCode = schoolBranch?.schoolBranchCode;

  useEffect(() => {
    dispatch(
      getStudents({
        branchCode,
        page: pagination.currentPage,
        size: pagination.pageSize,
        ...searchFilters,
      }),
    );
  }, [
    dispatch,
    pagination.currentPage,
    pagination.pageSize,
    searchFilters,
    branchCode,
  ]);

  const handleViewProfile = (admissionNumber: number) => {
    navigate(`/all-students/student-profile/${admissionNumber}`);
  };

  const tableData: Student[] = students.map((student) => ({
    ...student,
    id: student.admissionNumber,
  }));

  const columns: Column<Student>[] = [
    {
      label: "SL NO.",
      accessor: "studentAdmissionSequence",
      // searchable: true,
      render: (item) => (
        <p className="w-[50px]">{item?.studentAdmissionSequence || "----"}</p>
      ),
    },
    // {
    //   label: "Roll NO",
    //   accessor: "rollNo",
    //   searchable: true,
    //   render: (item) => <p>{item?.rollNo || "----"}</p>,
    // },
    {
      label: "Profile",
      accessor: "profile",
      render: (item) => (
        <div>
          {!item?.photoUrl ? (
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary-600 text-white font-semibold">
              {item.fullName.charAt(0).toUpperCase()}
            </div>
          ) : (
            <img
              src={item?.photoUrl}
              alt=""
              className="flex items-center justify-center object-cover w-10 h-10 rounded-full"
            />
          )}
        </div>
      ),
    },
    {
      label: "Student Name",
      accessor: "studentName",
      searchable: true,
      render: (item) => (
        <div>
          <div className="font-bold text-sm text-neutral-400">
            {item.fullName}
          </div>
          <div className="text-xs text-neutral-300">
            DOB - {item.dateOfBirth}
          </div>
        </div>
      ),
    },
    {
      label: "Parent Contact",
      accessor: "parentContact",
      searchable: true,
      render: (item) => (
        <div>
          <div className="text-neutral-400 text-[13px]">
            {item.motherEmail ||
              item.fatherEmail ||
              item.motherFullName ||
              item.fatherFullName}
          </div>
          <div className="text-neutral-400 text-[13px]">
            {item.motherPhoneNumber || item.fatherPhoneNumber}
          </div>
        </div>
      ),
    },
    {
      label: "Class",
      accessor: "className",
      searchable: true,
      render: (item) => <Tag text={item.academicCycle} />,
    },
    {
      label: "Action",
      accessor: "action",
      searchable: false,
      render: (item) => (
        <Tag
          size="sm"
          onClick={() => handleViewProfile(item.admissionNumber)}
          text="View Profile"
          color="warning"
        />
      ),
    },
  ];

  const handlePageChange = (newPage: number) => {
    dispatch(
      setPagination({ currentPage: newPage, pageSize: pagination.pageSize }),
    );
  };

  const handlePageSizeChange = (newSize: number) => {
    dispatch(setPagination({ currentPage: 0, pageSize: newSize }));
  };

  const handleDownload = () => {
    if (students && students?.length > 0) {
      exportToExcel(students ?? [], "studentList");
    }
  };

  return (
    <div className="flex flex-col gap-3">
      {/* <div className="flex flex-col md:flex-row md:justify md:items-center"> */}
      {/* <Tabs
          tabs={tabOptions}
          onTabChange={handleTabChange}
          activeTab={activeTab}
        /> */}
      <div className="flex gap-3 justify-end items-center">
        <PaginationBar
          currentPage={pagination.currentPage}
          pageSize={pagination.pageSize}
          totalItems={pagination.totalElements}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
        <DownloadButton
          onClick={() => handleDownload()}
          disabled={isLoading || students.length === 0}
        />
        {/* </div> */}
      </div>
      <div className="bg-white px-6 py-5 rounded-lg">
        <DataTable
          columns={columns}
          data={tableData}
          searchFilters={searchFilters}
          onSearchChange={(accessor, value) => {
            dispatch(setSearchFilters({ [accessor]: value }));
            dispatch(
              setPagination({ currentPage: 0, pageSize: pagination.pageSize }),
            );
          }}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
}
